// Token + return-to handoff. Tokens land in localStorage under the SAME key
// + shape that platform-frontend already reads (APP_TOKENS_LOCAL_STORAGE_KEY
// = "application_tokens", shape {accessToken, refreshToken}). When the user
// returns to twoshot.app after auth, platform-frontend's existing
// getApplicationTokens() picks them up unchanged.

import {getDefaultExternalReturnTo, isAllowedExternalLoginReturnTo} from "../../shared/login-return-to.ts";

const TOKEN_KEY = "application_tokens";
const RETURN_TO_KEY = "login_return_to";

export interface StoredTokens {
    accessToken: string;
    refreshToken: string;
}

export function saveTokens(t: {access_token: string; refresh_token: string}) {
    const stored: StoredTokens = {accessToken: t.access_token, refreshToken: t.refresh_token};
    localStorage.setItem(TOKEN_KEY, JSON.stringify(stored));
}

export function readTokens(): StoredTokens | null {
    const raw = localStorage.getItem(TOKEN_KEY);
    return raw ? JSON.parse(raw) : null;
}

export interface ReturnTarget {
    url: string;
    /** True when return_to is a same-origin OAuth2 path - go there directly,
     * the oauth_session cookie is already populated server-side. */
    internal: boolean;
}

function readStoredReturnTo(): string | null {
    try {
        return sessionStorage.getItem(RETURN_TO_KEY);
    } catch {
        return null;
    }
}

function writeStoredReturnTo(returnTo: string): void {
    try {
        sessionStorage.setItem(RETURN_TO_KEY, returnTo);
    } catch {
        /* sessionStorage may be unavailable in restricted browser modes */
    }
}

function resolveReturnTo(raw: string | null | undefined): ReturnTarget | null {
    if (!raw) return null;
    try {
        const u = new URL(raw, window.location.origin);
        if (u.origin === window.location.origin && u.pathname.startsWith("/oauth2/")) {
            return {url: u.toString(), internal: true};
        }
        if (isAllowedExternalLoginReturnTo(u)) {
            return {url: u.toString(), internal: false};
        }
    } catch { /* fall through */ }
    return null;
}

export function getReturnTo(returnToOverride?: string | null): ReturnTarget {
    const params = new URLSearchParams(window.location.search);
    const candidates = [
        returnToOverride,
        params.get("return_to"),
        readStoredReturnTo(),
    ];

    for (const candidate of candidates) {
        const target = resolveReturnTo(candidate);
        if (target) {
            writeStoredReturnTo(target.url);
            return target;
        }
    }

    return {url: getDefaultExternalReturnTo(), internal: false};
}
